import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { TextField, PasswordField } from '@/components/ui/TextField';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useSignup } from '@/auth/hooks';
import { errorMessage } from '@/lib/errorMessage';
import { ROUTES, MIN_PASSWORD_LENGTH } from '@/lib/config';
import { useT } from '@/i18n/useT';
import { rules } from './rules';

interface Form {
  name: string;
  email: string;
  password: string;
}

export function SignupPage() {
  const { t } = useT();
  const signup = useSignup();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>();

  const onSubmit = handleSubmit(async (data) => {
    setServerError('');
    try {
      await signup.mutateAsync(data);
      // No auto-login — the account must verify its email first. Send them to the
      // login screen with a "check your email" notice (and the address, so login
      // can offer a one-click resend).
      navigate(ROUTES.login, { replace: true, state: { justSignedUp: true, email: data.email } });
    } catch (e) {
      setServerError(errorMessage(e));
    }
  });

  return (
    <Card title={t('signup.title')} subtitle={t('signup.subtitle')}>
      <Alert kind="error">{serverError}</Alert>
      <form onSubmit={onSubmit} noValidate>
        <TextField
          label={t('common.name')}
          autoComplete="name"
          autoFocus
          error={errors.name?.message}
          disabled={signup.isPending}
          {...register('name', rules.name)}
        />
        <TextField
          label={t('common.email')}
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          disabled={signup.isPending}
          {...register('email', rules.email)}
        />
        <PasswordField
          label={t('common.password')}
          autoComplete="new-password"
          hint={t('signup.passwordHint', { min: MIN_PASSWORD_LENGTH })}
          error={errors.password?.message}
          disabled={signup.isPending}
          {...register('password', rules.newPassword)}
        />
        <Button type="submit" busy={signup.isPending}>
          {signup.isPending ? t('signup.working') : t('signup.cta')}
        </Button>
      </form>
      <p className="foot">
        {t('signup.haveAccount')}{' '}
        <Link className="link" to={ROUTES.login}>
          {t('signup.signIn')}
        </Link>
      </p>
    </Card>
  );
}
